/**
 * components/ui/CopyValue.js
 * ─────────────────────────────────────────────────────────────────────────────
 * A value an operator may need to quote or paste: a disbursement id, the
 * short reference on a rider's MoMo statement, MTN's financial transaction
 * id. Shows it (shortened when asked), carries the full value on hover, and
 * puts a copy control beside it so the true value is one click away.
 *
 * The control is a filled navy icon button (AppButton), because on this
 * dashboard that is what "clickable" looks like; the value itself is text.
 *
 *   <CopyValue value={d.disbursementId} max={20} mono />
 *   <CopyValue value={d.momoFinancialTxId} label="On the payee's SMS" />
 */
import React, { useCallback } from "react";
import AppButton from "./AppButton.js";
import { notify } from "../../utilities/notify.js";

const COPIED_TOAST = "copied-value";

export async function copyText(text) {
  const value = String(text ?? "");
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to the textarea route */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const CopyValue = ({ value, max, label, mono = true, strong = false, color, style, children }) => {
  const full = value == null ? "" : String(value);
  const shown = children ?? (max && full.length > max ? `${full.slice(0, max)}…` : full);

  const onCopy = useCallback(async (e) => {
    e.stopPropagation();
    const ok = await copyText(full);
    if (ok) notify.success("Copied", full, { id: COPIED_TOAST, autoClose: 2500 });
    else notify.error("Could not copy", "Select the value and copy it by hand.", { id: COPIED_TOAST });
  }, [full]);

  if (!full) return <span style={{ color: "var(--lk-text-light, #8898aa)" }}>n/a</span>;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%", ...style }}>
      <span
        title={full}
        style={{
          fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" : undefined,
          fontWeight: strong ? 600 : 400,
          fontSize: "inherit",
          color,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {shown}
      </span>
      {label ? <span style={{ fontSize: 11, color: "var(--lk-text-light, #8898aa)", whiteSpace: "nowrap" }}>{label}</span> : null}
      <AppButton size="sm" variant="quiet" icon="fa fa-copy" title={`Copy ${full}`} aria-label={`Copy ${full}`} onClick={onCopy} />
    </span>
  );
};

export default CopyValue;
